<script setup lang="ts">
  import type { PropType } from 'vue'

  import { computed, defineAsyncComponent, onBeforeUnmount, ref, useSlots } from 'vue'
  import { useI18n } from 'vue-i18n'

  import { v4 as uuidv4 } from 'uuid'

  import { Film01Icon, ImagePlusIcon, PlusCircleIcon } from '@gohighlevel/ghl-icons/24/outline'

  import { createElementId } from '@common/core-layer/utils'

  import type { ValidationError } from '@common/shared-ui-layer/types/validation.types'
  import { AspectRatio } from '../types'

  import { validateImageFiles } from '../utils/image.utils'

  import { validateVideoFiles } from '../utils/video.utils'

  import { COMPOSER_ACCEPTED_IMAGE_TYPES, COMPOSER_ACCEPTED_VIDEO_TYPES } from '../utils/constants'

  import type { MediaFile, MediaValidation } from '../types/media-picker.types'

  // Lazy load ImageCropperModal - only loads when cropping is needed
  const ImageCropperModal = defineAsyncComponent(() => import('./ImageCropperModal.vue'))

  const props = defineProps({
    disabled: {
      type: Boolean,
      default: false,
    },
    validation: {
      type: Object as PropType<MediaValidation>,
      default: undefined,
    },
    variant: {
      type: String as PropType<'image' | 'video' | 'both'>,
      default: 'both',
    },
    iconSize: {
      type: String as PropType<'2xs' | 'xs' | 'md' | 'lg'>,
      default: 'md',
    },
    parentClasses: {
      type: String,
      default: '',
    },
    /** Whether to allow selecting multiple files at once. Defaults to true for post/comment editors. Set to false for single file uploads like avatars. */
    multiple: {
      type: Boolean,
      default: true,
    },
    idPrefix: {
      type: String,
      default: 'media',
    },
    /** Enable image cropping before upload */
    enableCropping: {
      type: Boolean,
      default: false,
    },
    /** Aspect ratio for cropping (uses AspectRatio enum) */
    cropAspectRatio: {
      type: Number as PropType<AspectRatio>,
      required: false,
      default: AspectRatio.SQUARE,
    },
    /** Output width in pixels (optional - uses natural size if not provided) */
    outputWidth: {
      type: Number,
      required: false,
      default: undefined,
    },
    /** Output height in pixels (optional - uses natural size if not provided) */
    outputHeight: {
      type: Number,
      required: false,
      default: undefined,
    },
    /** Optional helper text shown below the default icon */
    helpText: {
      type: String,
      default: '',
    },
    /** Render default icon/help text even when a custom default slot is provided */
    showDefaultContentWithSlot: {
      type: Boolean,
      default: false,
    },
    /** When true, root shows token hover background (`hover:bg-hover`) while enabled. */
    enableHoverBackground: {
      type: Boolean,
      default: true,
    },
  })

  const emit = defineEmits<{
    filesSelected: [files: MediaFile[]]
    validationError: [errors: ValidationError[]]
  }>()

  // Get translation function
  const { t } = useI18n()

  // Cropper state
  const showCropperModal = ref(false)

  const pendingCropFile = ref<File | null>(null)
  const objectUrls = ref<string[]>([])
  const slots = useSlots()
  const createObjectUrl = (file: File) => {
    const objectUrl = URL.createObjectURL(file)
    objectUrls.value.push(objectUrl)
    return objectUrl
  }

  const cleanupObjectUrls = () => {
    objectUrls.value.forEach(url => URL.revokeObjectURL(url))
    objectUrls.value = []
  }

  const shouldRenderDefaultContent = computed(() => !slots.default || props.showDefaultContentWithSlot)

  // Default accepted types based on variant
  const defaultAcceptedTypes = computed(() => {
    switch (props.variant) {
      case 'image':
        return [...COMPOSER_ACCEPTED_IMAGE_TYPES]
      case 'video':
        return [...COMPOSER_ACCEPTED_VIDEO_TYPES]
      case 'both':
      default:
        return [...COMPOSER_ACCEPTED_IMAGE_TYPES, ...COMPOSER_ACCEPTED_VIDEO_TYPES]
    }
  })

  // Accept attribute for file input
  const acceptAttribute = computed(() => {
    if (props.validation) {
      // Combine accepted types from both image and video validations
      const imageTypes = props.validation.imageValidations?.acceptedTypes || []
      const videoTypes = props.validation.videoValidations?.acceptedTypes || []
      const allTypes = [...imageTypes, ...videoTypes]

      if (allTypes.length > 0) {
        return allTypes.join(',')
      }
    }

    return defaultAcceptedTypes.value.join(',')
  })

  const iconSizeClasses = computed(() => {
    switch (props.iconSize) {
      case '2xs':
        return 'w-4 h-4'
      case 'xs':
        return 'w-5 h-5'
      case 'md':
        return 'w-6 h-6'
      case 'lg':
        return 'w-7 h-7'
      default:
        return 'w-6 h-6'
    }
  })

  // File selection handler
  const handleFileSelect = () => {
    if (props.disabled) return

    const input = document.createElement('input')
    input.type = 'file'
    input.accept = acceptAttribute.value
    // Allow multiple files based on prop (default true for post/comment editors, false for single uploads like avatars)
    input.multiple = props.multiple

    input.onchange = (event: Event) => {
      const files = (event.target as HTMLInputElement).files
      if (files) {
        processFiles(Array.from(files))
      }
    }

    input.click()
  }

  // Detect file type
  const detectFileType = (file: File): 'image' | 'video' => {
    if (file.type.startsWith('image/')) return 'image'
    if (file.type.startsWith('video/')) return 'video'
    return 'image' // fallback
  }

  // Check if file type is allowed based on variant
  const isFileTypeAllowed = (fileType: 'image' | 'video'): boolean => {
    switch (props.variant) {
      case 'image':
        return fileType === 'image'
      case 'video':
        return fileType === 'video'
      case 'both':
      default:
        return true
    }
  }

  // Process selected files
  const processFiles = async (files: File[]) => {
    const allErrors: ValidationError[] = []
    const validFiles: File[] = []

    if (!props.validation) {
      // No validation, accept all files
      for (const file of files) {
        const fileType = detectFileType(file)
        if (isFileTypeAllowed(fileType)) {
          validFiles.push(file)
        }
      }
    } else {
      // Separate files by type
      const imageFiles = files.filter(file => detectFileType(file) === 'image')
      const videoFiles = files.filter(file => detectFileType(file) === 'video')

      // Validate images using shared utility (only file size, type, dimensions - maxFiles handled at composable level)
      if (imageFiles.length > 0 && props.validation.imageValidations) {
        const { valid, errors } = await validateImageFiles(imageFiles, props.validation.imageValidations)
        validFiles.push(...valid)
        allErrors.push(...errors)
      } else if (imageFiles.length > 0) {
        validFiles.push(...imageFiles)
      }

      // Validate videos using shared utility (only file size, type, duration - maxFiles handled at composable level)
      if (videoFiles.length > 0 && props.validation.videoValidations) {
        const { valid, errors } = await validateVideoFiles(videoFiles, props.validation.videoValidations)
        validFiles.push(...valid)
        allErrors.push(...errors)
      } else if (videoFiles.length > 0) {
        validFiles.push(...videoFiles)
      }

      // Custom validation
      if (props.validation.customValidator) {
        const filesToCheck = [...validFiles]
        validFiles.length = 0 // Clear

        for (const file of filesToCheck) {
          const customResult = props.validation.customValidator(file)
          if (customResult.valid) {
            validFiles.push(file)
          } else {
            allErrors.push({
              field: 'custom',
              message: customResult.error || `Custom validation failed for "${file.name}"`,
              type: 'error',
              meta: { fileName: file.name },
            })
          }
        }
      }
    }

    // Emit validation errors if any
    if (allErrors.length > 0) {
      emit('validationError', allErrors)
    }

    // Check if cropping is enabled for images
    if (validFiles.length > 0 && props.enableCropping && props.variant !== 'video') {
      // Separate images and videos
      const imageFiles = validFiles.filter(file => detectFileType(file) === 'image')
      const videoFiles = validFiles.filter(file => detectFileType(file) === 'video')

      // Handle videos normally (no cropping)
      if (videoFiles.length > 0) {
        const videoMediaFiles: MediaFile[] = videoFiles.map(file => ({
          _id: uuidv4(),
          file,
          url: createObjectUrl(file),
          type: 'video',
          name: file.name,
          size: file.size,
        }))
        emit('filesSelected', videoMediaFiles)
      }

      // Handle images with cropping (only first image if multiple is false)
      if (imageFiles.length > 0) {
        const fileToCrop = imageFiles[0] // Only crop first image
        pendingCropFile.value = fileToCrop
        showCropperModal.value = true
        return // Don't emit yet, wait for crop
      }
    }

    // No cropping or no images - proceed normally
    if (validFiles.length > 0) {
      const mediaFiles: MediaFile[] = validFiles.map(file => ({
        _id: uuidv4(),
        file,
        url: createObjectUrl(file),
        type: detectFileType(file),
        name: file.name,
        size: file.size,
      }))

      emit('filesSelected', mediaFiles)
    }

    // Run custom error handler if provided
    if (props.validation?.onValidationError && allErrors.length > 0) {
      props.validation.onValidationError(allErrors)
    }
  }

  // Handle cropped image (matching reference - receives { file, url } object)
  const handleCropped = (croppedData: { file: File; url: string }) => {
    if (!pendingCropFile.value) return

    const mediaFile: MediaFile = {
      _id: uuidv4(),
      file: croppedData.file,
      url: croppedData.url, // Use base64 data URL from cropper
      type: 'image',
      name: croppedData.file.name,
      size: croppedData.file.size,
    }

    emit('filesSelected', [mediaFile])
    showCropperModal.value = false
    pendingCropFile.value = null
  }

  // Handle crop cancel
  const handleCropCancel = () => {
    showCropperModal.value = false
    pendingCropFile.value = null
  }

  onBeforeUnmount(() => {
    cleanupObjectUrls()
  })
</script>

<template>
  <div
    :id="createElementId(props.idPrefix, 'picker', 'icon')"
    :class="['transition-colors', { 'hover:bg-hover': !disabled && enableHoverBackground }, parentClasses]"
    :data-enable-hover-background="enableHoverBackground ? 'true' : 'false'"
    role="button"
    tabindex="0"
    :aria-disabled="disabled ? 'true' : 'false'"
    :aria-label="t('media.addMedia')"
    :title="t('media.addMedia')"
    @click="handleFileSelect"
    @keydown.enter.prevent="handleFileSelect"
    @keydown.space.prevent="handleFileSelect"
  >
    <slot />
    <div v-if="shouldRenderDefaultContent && props.helpText" class="flex flex-col items-center justify-center gap-2 text-center px-4">
      <!-- Default icons based on variant -->
      <ImagePlusIcon
        v-if="variant === 'image'"
        class="cursor-pointer text-600 hover:text-800 disabled:cursor-not-allowed disabled:opacity-50"
        :class="{ 'cursor-not-allowed opacity-50': disabled, [iconSizeClasses]: iconSize }"
      />
      <Film01Icon
        v-else-if="variant === 'video'"
        class="cursor-pointer text-600 hover:text-800 disabled:cursor-not-allowed disabled:opacity-50"
        :class="{ 'cursor-not-allowed opacity-50': disabled, [iconSizeClasses]: iconSize }"
      />
      <PlusCircleIcon
        v-else
        class="cursor-pointer text-600 hover:text-800 disabled:cursor-not-allowed disabled:opacity-50"
        :class="{ 'cursor-not-allowed opacity-50': disabled, [iconSizeClasses]: iconSize }"
      />
      <p class="text-sm text-600">{{ props.helpText }}</p>
    </div>
    <!-- Default icons based on variant -->
    <ImagePlusIcon
      v-else-if="shouldRenderDefaultContent && variant === 'image'"
      class="cursor-pointer text-600 hover:text-800 disabled:cursor-not-allowed disabled:opacity-50"
      :class="{ 'cursor-not-allowed opacity-50': disabled, [iconSizeClasses]: iconSize }"
    />
    <Film01Icon
      v-else-if="shouldRenderDefaultContent && variant === 'video'"
      class="cursor-pointer text-600 hover:text-800 disabled:cursor-not-allowed disabled:opacity-50"
      :class="{ 'cursor-not-allowed opacity-50': disabled, [iconSizeClasses]: iconSize }"
    />
    <PlusCircleIcon
      v-else-if="shouldRenderDefaultContent"
      class="cursor-pointer text-600 hover:text-800 disabled:cursor-not-allowed disabled:opacity-50"
      :class="{ 'cursor-not-allowed opacity-50': disabled, [iconSizeClasses]: iconSize }"
    />
  </div>

  <!-- Image Cropper Modal -->
  <ImageCropperModal
    v-if="pendingCropFile"
    :show="showCropperModal"
    :image-file="pendingCropFile"
    :aspect-ratio="cropAspectRatio"
    :output-width="outputWidth"
    :output-height="outputHeight"
    @imageCropped="handleCropped"
    @cancel="handleCropCancel"
    @update:show="showCropperModal = $event"
  />
</template>
